import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { OrderChannel } from '../types/orders';

export interface OrderStatusChange {
  id: string;
  organization_id: string;
  order_id?: string;
  woocommerce_order_id?: string;
  order_channel: OrderChannel;
  order_number?: string;
  from_status?: string;
  to_status: string;
  changed_by?: string;
  changed_at: string;
  source: string;
  reason?: string;
  metadata?: Record<string, unknown>;
  // From view
  changed_by_name?: string;
  changed_by_email?: string;
}

interface OrderStatusHistoryStore {
  statusChanges: OrderStatusChange[];
  loading: boolean;
  error: string | null;

  // Fetch status history for an order
  fetchStatusHistory: (orderId: string, channel: OrderChannel) => Promise<void>;

  // Log a status change
  logStatusChange: (params: {
    orderId: string;
    channel: OrderChannel;
    orderNumber?: string;
    fromStatus?: string;
    toStatus: string;
    source?: string;
    reason?: string;
    metadata?: Record<string, unknown>;
  }) => Promise<OrderStatusChange | null>;

  // Utility
  getOrderStatusHistory: (orderId: string) => OrderStatusChange[];
  clearStatusHistory: () => void;
}

export const useOrderStatusHistoryStore = create<OrderStatusHistoryStore>((set, get) => ({
  statusChanges: [],
  loading: false,
  error: null,

  fetchStatusHistory: async (orderId: string, channel: OrderChannel) => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData?.organization_id) throw new Error('Organization not found');

      // Build query based on channel
      let query = supabase
        .from('order_status_history_view')
        .select('*')
        .eq('organization_id', profileData.organization_id)
        .order('changed_at', { ascending: false });

      if (channel === 'manual') {
        query = query.eq('order_id', orderId);
      } else {
        query = query.eq('woocommerce_order_id', orderId);
      }

      const { data, error } = await query.limit(50);

      if (error) throw error;
      set({ statusChanges: data || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  logStatusChange: async ({
    orderId,
    channel,
    orderNumber,
    fromStatus,
    toStatus,
    source = 'manual',
    reason,
    metadata
  }) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData?.organization_id) throw new Error('Organization not found');

      // Build insert data based on channel
      const insertData: Record<string, unknown> = {
        organization_id: profileData.organization_id,
        order_channel: channel,
        order_number: orderNumber,
        from_status: fromStatus,
        to_status: toStatus,
        changed_by: userData.user.id,
        source,
        reason,
        metadata
      };

      if (channel === 'manual') {
        insertData.order_id = orderId;
      } else {
        insertData.woocommerce_order_id = orderId;
      }

      const { data, error } = await supabase
        .from('order_status_history')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      const currentChanges = get().statusChanges;
      const updatedChanges = [data as OrderStatusChange, ...currentChanges];
      set({ statusChanges: updatedChanges });

      return data as OrderStatusChange;
    } catch (error) {
      console.error('Error logging status change:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  getOrderStatusHistory: (orderId: string) => {
    return get().statusChanges.filter(
      change => change.order_id === orderId || change.woocommerce_order_id === orderId
    );
  },

  clearStatusHistory: () => {
    set({ statusChanges: [], error: null });
  }
}));
